import MenuBarItemModel, { MenuGroupKey } from '../models/layout/MenuBarItemModel';

/** Lo que muestra la barra de íconos: los bloques más el panel de favoritos. */
export type MenuPanelKey = MenuGroupKey | 'favoritos';

export interface MenuGroupInfo {
    key: MenuGroupKey;
    /** Texto bajo el ícono de la barra (corto, cabe en 64 px). */
    railLabel: string;
    title: string;
    description: string;
    icon: string;
}

// El orden de este arreglo es el orden de la barra. "sistema" (Configuración)
// se dibuja aparte, al pie de la barra.
export const MENU_GROUPS: MenuGroupInfo[] = [
    { key: 'gerencia', railLabel: 'Gerencia', title: 'Gerencia', description: 'Políticas, objetivos y gestión gerencial', icon: 'fa-university' },
    { key: 'operaciones', railLabel: 'Operac.', title: 'Operaciones', description: 'Taller, laboratorio y terreno', icon: 'fa-industry' },
    // Seguridad sale de «Gestión» a bloque propio: es lo que más usa el
    // personal de planta y tenerlo a dos clics lo escondía.
    { key: 'seguridad', railLabel: 'Segurid.', title: 'Seguridad', description: 'Cursos, reportes flash y herramientas preventivas', icon: 'fa-exclamation-triangle' },
    { key: 'gestion', railLabel: 'Gestión', title: 'Gestión', description: 'Sistemas de gestión y soporte', icon: 'fa-briefcase' },
    { key: 'logistica', railLabel: 'Logística', title: 'Logística', description: 'Instalaciones, flota y recursos', icon: 'fa-cubes' },
    { key: 'sistema', railLabel: 'Config.', title: 'Configuración', description: 'Parámetros y administración del ERP', icon: 'fa-sliders' },
];

export const FAVORITOS_PANEL = {
    railLabel: 'Favoritos',
    title: 'Favoritos',
    description: 'Tus accesos directos',
    icon: 'fa-star',
};

export const getGroupInfo = (key: MenuGroupKey): MenuGroupInfo =>
    MENU_GROUPS.find((g) => g.key === key) as MenuGroupInfo;

/** Una pantalla navegable del menú, con la sección y el bloque al que pertenece. */
export interface MenuPantalla {
    href: string;
    name: string;
    icon?: string;
    seccion: string;
    group?: MenuGroupKey;
}

/**
 * Pantallas (hojas) del menú ya filtrado por roles. Una sección puede tener
 * subgrupos (p. ej. SGI → Gestión de Calidad); sus pantallas se aplanan con
 * la ruta de la sección como `seccion`.
 */
export const getPantallas = (items: MenuBarItemModel[]): MenuPantalla[] => {
    const hojas = (subMenu: MenuBarItemModel[], seccion: string, group?: MenuGroupKey): MenuPantalla[] =>
        subMenu.flatMap((sub) =>
            sub.subMenu
                ? hojas(sub.subMenu, `${seccion} › ${sub.name}`, group)
                : [{ href: sub.href, name: sub.name, icon: sub.icon, seccion, group }]
        );
    return items.flatMap((item) => (item.subMenu ? hojas(item.subMenu, item.name, item.group) : []));
};

/**
 * Solo se pueden marcar pantallas reales: "/en-construccion" la comparten
 * varios ítems y como favorito no distinguiría a cuál se refiere.
 */
export const esFavoritable = (href: string): boolean =>
    href.startsWith('/') && href !== '/' && href !== '/en-construccion';

const coincideRuta = (href: string, pathname: string): boolean =>
    pathname === href || pathname.startsWith(`${href}/`);

/** Pantalla del menú que corresponde a la URL actual (la coincidencia más larga). */
export const getPantallaActual = (pantallas: MenuPantalla[], pathname: string): MenuPantalla | undefined =>
    pantallas
        .filter((p) => esFavoritable(p.href) && coincideRuta(p.href, pathname))
        .sort((a, b) => b.href.length - a.href.length)[0];

export const esRutaActiva = (href: string, pathname: string): boolean =>
    esFavoritable(href) && coincideRuta(href, pathname);
